import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { Search, Lock, Paperclip, Play, X, ExternalLink, Sparkles, Check, Archive, Home, Inbox, ArrowDownAZ, Clock, Hash } from 'lucide-react'
import {
  artifacts,
  matters as mattersApi,
  results as resultsApi,
  runPipelineBulk,
  runPipelineForMatter,
  type Artifact,
  type ArtifactDetail,
  type Matter,
} from '../api'
import { useAuth } from '../auth'
import BodyPreview from '../components/BodyPreview'
import { copyArtifactToClipboard } from '../components/artifactCopy'
import { ChatBubble } from '../components/ChatPanel'
import ClassificationCard from '../components/ClassificationCard'
import EditableText from '../components/EditableText'
import MatterTimelinePane from '../components/MatterTimelinePane'
import {
  useScrollMemory,
  markScrollPendingRestore,
  clearScrollPendingRestore,
  rememberLastArtifact,
  recallLastArtifact,
  decideRememberAction,
  decideRecallAction,
} from '../components/scrollMemory'
import FolderSidebar, { MatterRowCompact } from '../components/FolderSidebar'

// URGENCY_LABELS retained for any future inline matter-row chip; for now
// the matter detail header uses a numeric urgency badge (5/5 etc.).
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const URGENCY_LABELS = ['', 'low', 'med-low', 'med', 'med-high', 'urgent']
void URGENCY_LABELS

export default function Workspace() {
  const auth = useAuth()
  const isAdmin = auth.status === 'authenticated' && (auth.user.is_staff || auth.user.is_superuser)
  // Tenant code for namespacing client-side state (folder open/close in
  // localStorage). Falls back to a sentinel for the cross-tenant superuser
  // case so we still get isolated state.
  const authedClientCode = (
    auth.status === 'authenticated' && auth.user.client?.code
  ) || '__unscoped__'
  // limit=500 — sidebar shows all matters in one scrollable list (no pager
  // pattern). With ~80 matters today and the AI generating ~5-10/day during
  // bulk runs, 500 is plenty of headroom. If we ever blow past 500 we'll
  // add real pagination here, but for now scrolling is simpler.
  const matters = useQuery({
    // Params live in the queryKey alongside the resource name so two
    // useQuery sites with different params can't corrupt each other's
    // cache slot (we hit this when ArtifactViewer fetched the default
    // 50-page list under bare `['matters']` while Workspace fetched
    // 500). invalidateQueries({queryKey: ['matters']}) still matches
    // both because react-query does prefix matching.
    queryKey: ['matters', { limit: 500 }],
    queryFn: () => mattersApi.list({ limit: 500 }),
    // Background poll keeps the sidebar + matter header fresh while
    // the user is sitting in Workspace. Pipeline writes update
    // Matter.summary / .stage / .waiting_on / .urgency from celery,
    // and other users may be editing concurrently. 20s is the budget
    // — ETag short-circuits unchanged polls (304, ~100 bytes) so the
    // server cost is small even with multiple active sessions.
    refetchInterval: 20_000,
  })
  // URL-backed selection state so back/forward and the artifact viewer's
  // back button restore exactly what was selected. ?matter=<id|inbox>
  // and ?artifact=<id>; defaults to inbox / no artifact.
  const [searchParams, setSearchParams] = useSearchParams()
  const selectedMatterId: number | 'inbox' | null = (() => {
    const v = searchParams.get('matter')
    if (v === null) return 'inbox'
    if (v === 'inbox') return 'inbox'
    if (v === '') return null
    const n = Number(v)
    return Number.isFinite(n) ? n : 'inbox'
  })()
  const selectedArtifactId: number | null = (() => {
    const v = searchParams.get('artifact')
    if (!v) return null
    const n = Number(v)
    return Number.isFinite(n) ? n : null
  })()
  const setSelectedMatterId = (id: number | 'inbox' | null) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev)
      if (id === null || id === 'inbox') {
        if (id === null) next.set('matter', '')
        else next.set('matter', 'inbox')
      } else {
        next.set('matter', String(id))
      }
      // Clear artifact when changing matters
      next.delete('artifact')
      return next
    }, { replace: false })
  }
  const setSelectedArtifactId = (id: number | null) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev)
      if (id == null) next.delete('artifact')
      else next.set('artifact', String(id))
      return next
    }, { replace: false })
  }
  // Folder-context for the currently-selected matter. Captures which
  // folder occurrence was clicked (a matter can appear in multiple
  // folders); back/reload restores the same folder path. NULL =
  // matter clicked from Unfiled or no folder context.
  const selectedFolderId: number | null = (() => {
    const v = searchParams.get('folder')
    if (!v) return null
    const n = Number(v)
    return Number.isFinite(n) ? n : null
  })()
  // Combined setter: clicking a matter in folder X sets matter + folder
  // atomically. Artifact is cleared only when switching to a DIFFERENT
  // matter — re-clicking the same matter from another folder context
  // (a matter filed in 2+ folders) preserves the artifact selection,
  // since the user is just navigating folder paths, not opening a
  // new matter.
  const setSelectedMatterInFolder = (matterId: number, folderId: number | null) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev)
      const prevMatter = prev.get('matter')
      const sameMatter = prevMatter === String(matterId)
      next.set('matter', String(matterId))
      if (folderId == null) next.delete('folder')
      else next.set('folder', String(folderId))
      if (!sameMatter) next.delete('artifact')
      return next
    }, { replace: false })
  }
  // Scroll memory — column 1 (matters list) restores ONLY when we're
  // returning from an ArtifactViewer round-trip (pending flag), so direct
  // visits to /workspace land at the top to surface new arrivals. Column
  // 2 (per-matter artifact list) restores per-matter always, so switching
  // between matters in-session feels like resuming context.
  const mattersListRef = useRef<HTMLElement>(null)
  const inboxListRef = useRef<HTMLElement>(null)
  // Sidebar scroll restore needs to wait for the matters list to load
  // before the saved scrollTop has any meaningful target. On a refresh,
  // useScrollMemory now also activates restore (via isPageReload) — but
  // attempting to set scrollTop on an empty <aside> is a no-op, so we
  // gate on matters.data being present.
  useScrollMemory(mattersListRef, 'me.scroll.matters', 'pending', !!matters.data)
  useScrollMemory(
    inboxListRef,
    `me.scroll.artifacts.${selectedMatterId === 'inbox' ? 'inbox' : selectedMatterId ?? 'none'}`,
    'always',
  )
  // Clear the pending flag once we've had a chance to consume it. Without
  // this a stray refresh later would still trigger restore.
  useEffect(() => {
    const t = window.setTimeout(() => clearScrollPendingRestore(), 100)
    return () => window.clearTimeout(t)
  }, [])

  // Persist last-selected artifact per (matter|inbox). Decision logic
  // (the part that's burned us with race conditions twice) lives in
  // decideRememberAction in scrollMemory.ts and is unit-tested. This
  // effect just wires inputs in and dispatches the resulting action.
  const prevMatterIdRef = useRef<string | number | null>(null)
  useEffect(() => {
    const action = decideRememberAction({
      prevMatterId: prevMatterIdRef.current,
      currentMatterId: selectedMatterId,
      currentArtifactId: selectedArtifactId,
    })
    prevMatterIdRef.current = selectedMatterId
    if (selectedMatterId === null) return
    const matterScope = selectedMatterId === 'inbox' ? 'inbox' : selectedMatterId
    if (action === 'store' && selectedArtifactId) {
      rememberLastArtifact(matterScope, selectedArtifactId)
    } else if (action === 'clear') {
      rememberLastArtifact(matterScope, null)
    }
  }, [selectedMatterId, selectedArtifactId])

  const [matterFilter, setMatterFilter] = useState('')
  const [matterSort, setMatterSort] = useState<'recent_desc' | 'alpha' | 'code'>(
    () => {
      const s = (typeof localStorage !== 'undefined' && localStorage.getItem('me_matter_sort')) || ''
      // Legacy values (recent_asc) fall through to recent_desc; the
      // "oldest first" mode was retired.
      return s === 'alpha' || s === 'code' ? s : 'recent_desc'
    },
  )
  // Snapshot of matter ids the user has already "seen" (i.e. already
  // had a chance to notice). Initial value = whatever matters existed
  // when the workspace first loaded. We add to it whenever a brand-new
  // matter's row has rendered+flashed once, so a subsequent collapse/
  // re-expand of its folder (which unmounts/remounts the row) doesn't
  // re-fire the flash. "Flash exactly once per (page-load, matter-id)."
  const [seenAtLoad, setSeenAtLoad] = useState<Set<number> | null>(null)
  useEffect(() => {
    if (seenAtLoad === null && matters.data) {
      setSeenAtLoad(new Set(matters.data.map((m) => m.id)))
    }
  }, [matters.data, seenAtLoad])
  const markSeen = (id: number) => {
    setSeenAtLoad((prev) => {
      if (prev === null || prev.has(id)) return prev
      const next = new Set(prev)
      next.add(id)
      return next
    })
  }

  // Bulk-select state, scoped to the current artifact list
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set())
  const [lastRunMsg, setLastRunMsg] = useState<string | null>(null)

  const runBulk = useMutation({
    mutationFn: (ids: number[]) => runPipelineBulk(ids),
    onSuccess: (data) => {
      setLastRunMsg(`Enqueued ${data.enqueued}${data.rejected ? `, ${data.rejected} rejected (cross-tenant)` : ''}.`)
      setSelectedIds(new Set())
    },
  })
  const runForMatter = useMutation({
    mutationFn: (matterId: number) => runPipelineForMatter(matterId),
    onSuccess: (data) => setLastRunMsg(`Enqueued ${data.enqueued} for matter #${data.matter_id}.`),
  })

  // Matter view: per-matter list. Bumped to limit=500 so a matter
  // with many artifacts isn't silently truncated to the DRF page size
  // of 50. (Per-matter pagination can come if a matter ever exceeds
  // 500 artifacts; today they don't.)
  const matterArtifacts = useQuery({
    queryKey: ['artifacts', 'matter', selectedMatterId],
    queryFn: () =>
      typeof selectedMatterId === 'number'
        ? artifacts.list({ matter: selectedMatterId, limit: 500 })
        : Promise.resolve([]),
    enabled: typeof selectedMatterId === 'number',
    // Background poll: pipeline writes ArtifactMatter rows from celery
    // (AI files an artifact into this matter) while the user sits here.
    // Only fires for the OPEN matter — react-query polls mounted+enabled
    // queries only, and `enabled` is gated on the selected id.
    refetchInterval: 30_000,
    // No keepPreviousData here — when switching matters, we want the
    // list to clear and the spinner to take over. Showing the previous
    // matter's content (even dimmed) was potentially confusing now that
    // we have a clear loading indicator. Hover-prefetch on matter rows
    // means the data is usually already warm by the time the user
    // clicks, so the empty state is brief in practice.
  })


  // Inbox view: paginated, prev/next/+10/-10 (matches the artifacts
  // browser pattern). Server-side filter has_matter=false +
  // ignored=false (default). Order: newest source_date first; secondary
  // by id ASC so an email and its attachments cluster (attachments
  // inherited the email's source_date at import + via the backfill
  // SQL, and their ids are higher than the parent's since emails
  // import before attachments).
  const INBOX_PAGE_SIZE = 50
  const [inboxOffset, setInboxOffset] = useState(0)
  // Reset offset when switching INTO inbox view.
  useEffect(() => {
    if (selectedMatterId === 'inbox') setInboxOffset(0)
  }, [selectedMatterId])

  const inboxQuery = useQuery({
    queryKey: ['inbox-page', inboxOffset],
    queryFn: () =>
      artifacts.page({
        has_matter: 'false',
        limit: INBOX_PAGE_SIZE,
        offset: inboxOffset,
        ordering: '-source_date,id',
      }),
    enabled: selectedMatterId === 'inbox',
    placeholderData: keepPreviousData,
    // Background poll: import + auto-classification writes new
    // unfiled artifacts while the user sits in the inbox view.
    // Same gating story as matterArtifacts above.
    refetchInterval: 30_000,
  })
  const inboxTotal = inboxQuery.data?.count ?? 0
  const inboxItems: Artifact[] = inboxQuery.data?.results ?? []
  const inboxLastPageOffset = inboxTotal === 0 ? 0 : (Math.ceil(inboxTotal / INBOX_PAGE_SIZE) - 1) * INBOX_PAGE_SIZE
  const inboxJump = (deltaPages: number) => {
    const target = Math.max(0, Math.min(inboxLastPageOffset, inboxOffset + deltaPages * INBOX_PAGE_SIZE))
    setInboxOffset(target)
  }

  // Unified list the rest of the file consumes. `inboxOnly` keeps its
  // historical name for diff-clarity; it's just "the visible
  // artifacts list" regardless of matter / inbox.
  const inboxOnly: Artifact[] = useMemo(() => {
    if (typeof selectedMatterId === 'number') return matterArtifacts.data ?? []
    if (selectedMatterId === 'inbox') return inboxItems
    return []
  }, [selectedMatterId, matterArtifacts.data, inboxItems])

  // Auto-restore last-selected artifact on matter change — but only if
  // it's still in the matter's visible list. If the artifact was
  // re-routed to a different matter, marked ignored, or otherwise no
  // longer belongs here, leave selection empty so the third pane shows
  // its empty state instead of an orphan artifact.
  //
  // Wait until the list query has actually returned a result (loading
  // state shows []) before deciding membership — otherwise we'd skip
  // the restore on first render every single time.
  const matterListLoaded =
    typeof selectedMatterId === 'number'
      ? !matterArtifacts.isLoading && !!matterArtifacts.data
      : selectedMatterId === 'inbox'
        ? !inboxQuery.isLoading && !!inboxQuery.data
        : false
  // Auto-recall the last-viewed artifact for this matter. Decision logic
  // (when to fire, when to wait, when to skip) lives in decideRecallAction
  // in scrollMemory.ts and is unit-tested. This effect resolves inputs
  // and dispatches the result.
  const recallAttemptedFor = useRef<string | number | null>(null)
  useEffect(() => {
    const matterScope = selectedMatterId === 'inbox' ? 'inbox' : selectedMatterId
    const dataLoaded = selectedMatterId === 'inbox'
      ? !inboxQuery.isLoading
      : selectedMatterId !== null
        ? !matterArtifacts.isLoading
        : false
    const action = decideRecallAction({
      selectedMatterId,
      selectedArtifactId,
      matterListLoaded,
      artifactsLoaded: dataLoaded,
      storedArtifactId: matterScope ? recallLastArtifact(matterScope) : null,
      visibleArtifactIds: new Set(inboxOnly.map((a) => a.id)),
      recallAttemptedFor: recallAttemptedFor.current,
    })
    if (action.kind === 'wait') return
    // Mark BEFORE applying so a no-op outcome doesn't keep retrying. (The
    // recallAttemptedFor ref guards against background-refetch loops.)
    if (selectedMatterId !== null) recallAttemptedFor.current = selectedMatterId
    if (action.kind === 'select') {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev)
        next.set('artifact', String(action.artifactId))
        return next
      }, { replace: true })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMatterId, matterListLoaded, inboxOnly])

  const detail = useQuery({
    queryKey: ['artifact', selectedArtifactId],
    queryFn: () => (selectedArtifactId ? artifacts.detail(selectedArtifactId) : null),
    enabled: !!selectedArtifactId,
  })
  const detailResults = useQuery({
    queryKey: ['results', 'artifact', selectedArtifactId],
    queryFn: () => (selectedArtifactId ? resultsApi.list({ artifact: selectedArtifactId }) : Promise.resolve([])),
    enabled: !!selectedArtifactId,
  })
  // Routing assessments removed from the workspace right pane; they
  // live in Studio for prompt-iteration debugging.

  const queryClient = useQueryClient()

  // Hover-prefetch helper retained for future re-introduction of hover
  // prefetching on FolderSidebar matter rows (currently unused after the
  // flat list was replaced by the folder tree). Touch-noop'd via void
  // to satisfy noUnusedLocals.
  const prefetchMatterArtifacts = (matterId: number) => {
    queryClient.prefetchQuery({
      queryKey: ['artifacts', 'matter', matterId],
      queryFn: () => artifacts.list({ matter: matterId, limit: 500 }),
      staleTime: 30_000,
    }).catch(() => { /* best effort */ })
  }
  void prefetchMatterArtifacts

  const updateMatter = useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: Partial<Matter> }) =>
      mattersApi.update(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['matters'] })
      // Folder counts (matter_count_self + matter_count_recursive) exclude
      // archived matters, so archive/restore changes the chip values.
      // Without this, the sidebar tree shows stale counts until the
      // 30s staleTime expires.
      queryClient.invalidateQueries({ queryKey: ['folders', 'tree'] })
    },
  })

  const filteredMatters = useMemo(() => {
    if (!matters.data) return []
    const base = !matterFilter.trim() ? matters.data : (() => {
      const q = matterFilter.toLowerCase()
      return matters.data.filter(
        (m) =>
          m.name.toLowerCase().includes(q) ||
          m.matter_type.toLowerCase().includes(q) ||
          (m.composite_id || '').toLowerCase().includes(q) ||
          (m.summary || '').toLowerCase().includes(q),
      )
    })()
    // Sort. Three modes:
    //   recent_desc: newest activity first (updated_at desc) — the proxy
    //     for "most recent activity" bumps on status change, summary
    //     update, pipeline writeback, etc.
    //   alpha:       A → Z by display name
    //   code:        M-### ascending (client_seq, creation order)
    const sorted = [...base]
    if (matterSort === 'alpha') {
      sorted.sort((a, b) => a.name.localeCompare(b.name))
    } else if (matterSort === 'code') {
      sorted.sort((a, b) => (a.client_seq || 0) - (b.client_seq || 0))
    } else {
      sorted.sort((a, b) => {
        const av = a.updated_at || a.created_at
        const bv = b.updated_at || b.created_at
        return bv.localeCompare(av)
      })
    }
    return sorted
  }, [matters.data, matterFilter, matterSort])

  // Proposed section deprecated; computed but unused. Kept for now in
  // case we revive the flow.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const proposedMatters = useMemo(
    () => filteredMatters.filter((m) => m.status === 'proposed'),
    [filteredMatters],
  )
  const openMatters = useMemo(
    () => filteredMatters.filter(
      (m) => m.status === 'intake' || m.status === 'active' || m.status === 'on_hold',
    ),
    [filteredMatters],
  )
  const archivedMatters = useMemo(
    () => filteredMatters.filter((m) => m.status === 'closed'),
    [filteredMatters],
  )

  // Archived section open-state — auto-opens when a search filter
  // matches archived matters; restores the pre-search open state when
  // the filter clears.
  //
  // The pre-search snapshot is held in a ref (not state) so toggling
  // the archive while search is active doesn't re-snapshot, and so
  // toggling outside of search doesn't trigger the effect to overwrite
  // the snapshot.
  const [archiveOpen, setArchiveOpen] = useState(false)
  const archivePreSearchOpen = useRef<boolean | null>(null)
  // Ref on the <details> so we can scroll it into view when the user
  // explicitly opens the archived section. Without this the expanded
  // rows render below the visible scroll area.
  const archiveDetailsRef = useRef<HTMLDetailsElement>(null)
  useEffect(() => {
    const searching = !!matterFilter.trim()
    if (searching) {
      if (archivePreSearchOpen.current === null) {
        // Just started searching — capture the current state to
        // restore later. Subsequent search edits don't re-snapshot.
        archivePreSearchOpen.current = archiveOpen
      }
      if (archivedMatters.length > 0) setArchiveOpen(true)
    } else if (archivePreSearchOpen.current !== null) {
      // Search cleared — restore.
      setArchiveOpen(archivePreSearchOpen.current)
      archivePreSearchOpen.current = null
    }
    // archiveOpen intentionally not in deps: we only want to snapshot
    // at the moment search BECOMES active, not whenever the user
    // toggles the section.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matterFilter, archivedMatters.length])

  // Auto-open Archived when the selected matter is itself archived.
  // Otherwise hitting a deep-link / refresh / back-nav with an archived
  // matter selected lands on a workspace where the matter detail is
  // visible in column 2 but the row is hidden inside a closed Archived
  // section. The user can still close it manually after — we only
  // FORCE open, we don't fight subsequent toggles.
  useEffect(() => {
    if (typeof selectedMatterId !== 'number') return
    const m = matters.data?.find((x) => x.id === selectedMatterId)
    if (m && m.status === 'closed') setArchiveOpen(true)
  }, [selectedMatterId, matters.data])

  // Clear the URL's `folder` param when it's stale — the selected
  // matter is no longer in that folder (dismissed via picker, folder
  // deleted, etc.). Without this, the FolderSidebar auto-expand effect
  // walks up from the stale selectedFolderId on every refresh and
  // re-opens folders the user explicitly closed. Symptom: close test2,
  // refresh, test2 reopens because URL still has ?folder=test2_id from
  // before you removed the matter from it.
  useEffect(() => {
    if (selectedFolderId == null) return
    if (typeof selectedMatterId !== 'number') return
    const m = matters.data?.find((x) => x.id === selectedMatterId)
    if (!m) return  // matter not loaded yet — don't clear, would lose context
    const inFolder = (m.folder_assignments || []).some((a) => a.folder === selectedFolderId)
    if (inFolder) return
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev)
      next.delete('folder')
      return next
    }, { replace: true })
  }, [selectedMatterId, selectedFolderId, matters.data, setSearchParams])

  const cycleSort = () => {
    const next: typeof matterSort = matterSort === 'recent_desc' ? 'alpha'
              : matterSort === 'alpha' ? 'code'
              : 'recent_desc'
    setMatterSort(next)
    try { localStorage.setItem('me_matter_sort', next) } catch { /* ignore */ }
  }
  // Sort cycler rendered next to the search input below. Cycles
  // recent_desc → recent_asc → alpha → recent_desc. We pass the
  // sorted matter list through to FolderSidebar, which preserves
  // input order when grouping into folders — so this control
  // governs the order within Unfiled and within each folder.

  // Inbox count chip (sidebar). Cheap server-side count regardless
  // of which matter is currently selected, so the sidebar's
  // "Inbox · unfiled · N" stays accurate even when we're not in inbox
  // view. Pulls only one row (we just need .count).
  const inboxCountQuery = useQuery({
    queryKey: ['inbox-count'],
    queryFn: () => artifacts.page({ has_matter: 'false', limit: 1 }),
    refetchInterval: 30_000,  // keep fresh while AI is filing artifacts
  })
  const inboxCount = inboxCountQuery.data?.count ?? 0

  const selectedMatter = typeof selectedMatterId === 'number'
    ? matters.data?.find((m) => m.id === selectedMatterId)
    : null
  // If the URL asks for a matter we don't have in cached data yet,
  // kick a refetch immediately rather than waiting for the next
  // background poll. Otherwise the spinner could sit for up to 60s
  // (the polling interval) — particularly noticeable when the user
  // navigates from /artifact/N back to /workspace?matter=N and the
  // cached matters list happens to be stale.
  useEffect(() => {
    if (typeof selectedMatterId === 'number' && !selectedMatter && !matters.isFetching) {
      matters.refetch()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMatterId, selectedMatter])
  // Chat scope: matter when one is open, otherwise global. Selecting an
  // artifact inside a matter doesn't change the scope — the matter
  // conversation just learns which artifact you're looking at (passed
  // separately as "current view" context). Inbox / All artifacts both
  // share the same global thread.
  const chatScopeKind: 'matter' | 'global' =
    typeof selectedMatterId === 'number' ? 'matter' : 'global'
  const chatScopeId = chatScopeKind === 'matter' ? (selectedMatterId as number) : null
  const chatScopeLabel = chatScopeKind === 'matter'
    ? selectedMatter?.name ?? null
    : null

  return (
    <div className="relative grid h-full grid-cols-[320px_minmax(380px,460px)_1fr] gap-px bg-[color:var(--me-border)]">
      {/* Sidebar — clicks on the empty padding (not on a row) deselect
          everything and drop chat back to the global thread.
          setSelectedMatterId already deletes `artifact` from the URL,
          so we only call that one — calling both in the same handler
          races on react-router-v6's setSearchParams. */}
      <aside
        ref={mattersListRef}
        className="me-scroll overflow-auto bg-[color:var(--me-surface)] p-3"
        onClick={(e) => {
          if (e.target === e.currentTarget) setSelectedMatterId(null)
        }}
      >
        <div className="mb-3 flex items-stretch gap-1.5">
          <div className="relative flex-1 min-w-0">
            <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[color:var(--me-textMute)]" />
            <input
              placeholder="Search matters…"
              value={matterFilter}
              onChange={(e) => setMatterFilter(e.target.value)}
              className="me-input text-[12px]"
              style={{ paddingLeft: 28 }}
            />
          </div>
          {/* Sort cycler. Cycles recent_desc → recent_asc → alpha → … and
              persists choice to localStorage so it survives reload. Icon
              reflects the current mode so the user can tell at a glance
              which order they're in. */}
          <button
            onClick={cycleSort}
            className="me-btn me-btn-outline !text-[11px] !px-2 shrink-0"
            title={
              matterSort === 'recent_desc'
                ? 'Sort: newest activity first (click for A → Z)'
                : matterSort === 'alpha'
                ? 'Sort: A → Z (click for M-### ascending)'
                : 'Sort: M-### ascending (click for newest first)'
            }
          >
            {matterSort === 'recent_desc' ? <Clock size={12} /> :
             matterSort === 'alpha' ? <ArrowDownAZ size={12} /> :
             <Hash size={12} />}
          </button>
        </div>

        {/* Home — explicit "no matter selected" entry. Drops the chat
            into its global thread (scope=null), clears the artifact
            pane, and gives the user a guaranteed way to escape a
            scrolled-down matters list. Click on the empty sidebar
            padding does the same thing but isn't discoverable.
            NOTE: only one setSearchParams call here — setSelectedMatterId
            already deletes `artifact` from the URL. Calling
            setSelectedArtifactId in the same handler caused a stale-
            prev race with react-router-v6 and intermittently overwrote
            the matter clear. */}
        <button
          onClick={() => setSelectedMatterId(null)}
          data-selected={selectedMatterId === null ? 'true' : undefined}
          className="me-row mb-1"
          title="Clear matter selection — chat in global mode"
        >
          <div className="flex items-center gap-1.5">
            <Home size={12} className="me-text-mute" />
            <span className="font-medium me-text">Home</span>
          </div>
          <div className="me-mono mt-0.5 text-[11px] me-text-mute">no matter selected · global chat</div>
        </button>
        <button
          onClick={() => setSelectedMatterId('inbox')}
          data-selected={selectedMatterId === 'inbox' ? 'true' : undefined}
          className="me-row mb-1"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1.5">
              <Inbox size={12} className="me-text-mute" />
              <span className="font-medium me-text">Inbox · unfiled</span>
            </div>
            <span className="me-chip me-mono">{inboxCount.toLocaleString()}</span>
          </div>
          <div className="me-mono mt-0.5 text-[11px] me-text-mute">artifacts not yet on a matter</div>
        </button>
        {/* Proposed section preserved-but-disabled 2026-05-07.
            History: pre-folders, new AI-created matters landed
            status='proposed' and waited for the user to Keep or Archive
            them via the Sparkles-marked section here. With folders
            shipped, the user's recourse for unwanted matters is to
            archive (or never visit them) — no need for a separate
            approval queue. The runner.py L3 step now defaults new
            matters to status='active'.
            To revive: flip `false` below to (e.g.) `client.ai_matter_default_status === 'proposed'`
            and change apps/pipeline/runner.py's `defaults={'status': 'active'}`
            back to 'proposed'. Could also become a per-client toggle —
            risk-averse firms might want the human-approval gate. */}
        {false && proposedMatters.length > 0 && (
          <>
            <div className="me-section-h mt-4 flex items-center gap-1">
              <Sparkles size={11} className="me-text-mute" />
              <span>Proposed · {proposedMatters.length.toLocaleString()}</span>
            </div>
            {proposedMatters.map((m) => (
              <ProposedMatterRow
                key={m.id}
                matter={m}
                selected={selectedMatterId === m.id}
                onSelect={() => setSelectedMatterId(m.id)}
                onKeep={() => updateMatter.mutate({ id: m.id, payload: { status: 'intake' } })}
                onArchive={() => updateMatter.mutate({ id: m.id, payload: { status: 'closed' } })}
                pending={updateMatter.isPending}
              />
            ))}
          </>
        )}

        {/* Folder tree — replaces the old flat "Matters · N" list. Matters
            appear nested under their folders (one occurrence per folder
            they're in); matters in zero folders show in the synthetic
            "Unfiled" section at the top of the tree. The flat sort
            controls (recent / oldest / alpha) only apply WITHIN a folder
            list; we leave them off the new sidebar for now. */}
        <div className="mt-3">
          <FolderSidebar
            matters={openMatters}
            selectedMatterId={selectedMatterId}
            selectedFolderId={selectedFolderId}
            onSelectMatter={setSelectedMatterInFolder}
            onArchiveMatter={(id) => updateMatter.mutate({ id, payload: { status: 'closed' } })}
            clientCode={authedClientCode}
            searchActive={!!matterFilter.trim()}
            seenAtLoad={seenAtLoad}
            markSeen={markSeen}
          />
        </div>
        {filteredMatters.length === 0 && (
          <div className="px-2 pt-1 text-[12px] me-text-mute">no matches</div>
        )}

        {archivedMatters.length > 0 && (
          <details
            ref={archiveDetailsRef}
            className="mt-4"
            open={archiveOpen}
            onToggle={(e) => {
              const wasOpen = archiveOpen
              const isOpen = e.currentTarget.open
              setArchiveOpen(isOpen)
              // When the user explicitly opens the section, scroll its
              // header to the top of the sidebar — otherwise the
              // expanded rows often render below the viewport and the
              // user can't see what they just opened. Skip on close
              // (no point) and skip auto-opens triggered by a search
              // filter (those happen mid-typing; jumping the scroll
              // would feel rude). The 'wasOpen → isOpen' transition
              // catches the user-driven case specifically.
              if (!wasOpen && isOpen) {
                // Defer to next frame so the rows are in the DOM before
                // we measure scroll. Use the ref (not e.currentTarget)
                // — the synthetic event may be reused by then.
                requestAnimationFrame(() => {
                  archiveDetailsRef.current?.scrollIntoView({
                    behavior: 'smooth', block: 'start',
                  })
                })
              }
            }}
          >
            <summary className="me-section-h !mb-0 flex items-center gap-1 cursor-pointer hover:me-text">
              <Archive size={11} className="me-text-mute" />
              <span>Archived · {archivedMatters.length.toLocaleString()}</span>
            </summary>
            <div className="mt-1">
              {archivedMatters.map((m) => (
                <MatterRowCompact
                  key={m.id}
                  matter={m}
                  selected={selectedMatterId === m.id}
                  onSelect={() => setSelectedMatterId(m.id)}
                  onRestore={() => updateMatter.mutate({ id: m.id, payload: { status: 'intake' } })}
                  archived
                />
              ))}
            </div>
          </details>
        )}
      </aside>

      {/* Artifact list — when a matter is selected, the new
          MatterTimelinePane (matter header + Spine/Stages/Feed views)
          takes over. For inbox / no-selection, fall back to the
          existing flat list with bulk-toolbar.
          Loading guard: any time the URL says we want a matter
          (selectedMatterId is a number) but we don't yet have it in
          matters.data, show a spinner. The previous version only
          caught matters.isLoading — that's true on the FIRST fetch
          but false during background refetches when react-query is
          serving stale cached data. So a back-button into
          /workspace?matter=N&artifact=X could land momentarily on
          the inbox/home fallback (no header, empty list, but with
          the artifact still selected on the right) until matters
          refetched ~60s later. Now we keep showing the spinner until
          the matter actually appears in matters.data. The very rare
          edge case where a matter is genuinely missing (deleted /
          privacy-stripped) gets a stuck spinner, which is better
          than the inbox-flash UX. */}
      {typeof selectedMatterId === 'number' && !selectedMatter ? (
        <section className="me-scroll relative overflow-hidden bg-[color:var(--me-surface)]">
          <div
            className="pointer-events-none absolute inset-0 z-10 flex items-start justify-center pt-16"
            aria-label="Loading matter"
            role="status"
          >
            <div className="size-8 rounded-full border-[3px] border-[color:var(--me-textMute)] border-t-transparent animate-spin" />
          </div>
        </section>
      ) : selectedMatter ? (
        <MatterTimelinePane
          matter={selectedMatter}
          artifacts={inboxOnly}
          selectedArtifactId={selectedArtifactId}
          onSelectArtifact={(id) => setSelectedArtifactId(id)}
          scrollKey={`me.scroll.artifacts.${selectedMatter.id}`}
          // Use isPending (not isFetching) so the spinner only appears
          // when we genuinely have no data to show — on first load or
          // when switching matters. Background refetches (e.g. focus
          // refresh) where data already exists don't trigger the
          // overlay; they swap silently.
          isFetching={matterArtifacts.isPending && typeof selectedMatterId === 'number'}
          // No bulk-action toolbar in the matter view — bulk-run lives
          // in Studio. The matter view stays focused on reading.
        />
      ) : (
      <section ref={inboxListRef} className="me-scroll overflow-auto bg-[color:var(--me-surface)] p-3">
        <div className="mb-2 flex items-center justify-between">
          <div className="me-section-h !mb-0">
            {selectedMatterId === 'inbox' && 'Inbox · unfiled'}
            {selectedMatterId === null && 'Home · no matter selected'}
          </div>
          <span className="me-mono text-[11px] me-text-mute">
            {selectedMatterId === 'inbox' && inboxTotal > inboxOnly.length
              ? `${inboxOnly.length.toLocaleString()} of ${inboxTotal.toLocaleString()}`
              : inboxOnly.length.toLocaleString()}
          </span>
        </div>

        {/* Bulk-action toolbar */}
        <div className="mb-2 flex flex-wrap items-center gap-2 rounded-sm border me-border bg-[color:var(--me-bg)] px-2 py-1.5 text-[11px]">
          <button
            onClick={() => {
              if (selectedIds.size === inboxOnly.length) setSelectedIds(new Set())
              else setSelectedIds(new Set(inboxOnly.map((a) => a.id)))
            }}
            className="me-btn me-btn-outline"
            title={selectedIds.size === inboxOnly.length ? 'Deselect all' : 'Select all visible'}
          >
            {selectedIds.size === inboxOnly.length && inboxOnly.length > 0 ? 'Deselect all' : 'Select all'}
          </button>
          <span className="me-mono me-text-mute">{selectedIds.size} selected</span>
          {isAdmin && (
            <button
              onClick={() => runBulk.mutate(Array.from(selectedIds))}
              disabled={selectedIds.size === 0 || runBulk.isPending}
              className="me-btn me-btn-primary"
            >
              <Play size={11} />
              {runBulk.isPending ? 'Enqueuing…' : 'Run on selected'}
            </button>
          )}
          {isAdmin && typeof selectedMatterId === 'number' && (
            <button
              onClick={() => runForMatter.mutate(selectedMatterId)}
              disabled={runForMatter.isPending}
              className="me-btn me-btn-outline ml-auto"
              title="Run pipeline against every artifact in this matter"
            >
              {runForMatter.isPending ? 'Enqueuing…' : 'Run pipeline on whole matter'}
            </button>
          )}
          {lastRunMsg && (
            <span className="me-chip me-chip-fresh">
              {lastRunMsg}
              <button onClick={() => setLastRunMsg(null)} className="ml-1" title="dismiss">
                <X size={9} />
              </button>
            </span>
          )}
        </div>

        {/* Top pagination — duplicated above the rows so the user
            doesn't have to scroll to flip pages. State is shared with
            the bottom pager (single inboxOffset), so they stay in sync
            for free. */}
        {selectedMatterId === 'inbox' && inboxTotal > 0 && (
          <div className="mb-2">
            <InboxPager
              offset={inboxOffset}
              pageSize={INBOX_PAGE_SIZE}
              total={inboxTotal}
              hasNext={!!inboxQuery.data?.next}
              onJump={inboxJump}
            />
          </div>
        )}
        <div
          className={`transition-opacity ${
            selectedMatterId === 'inbox' && inboxQuery.isFetching && inboxQuery.isPlaceholderData
              ? 'opacity-60'
              : ''
          }`}
        >
          {inboxOnly.map((a) => (
            <ArtifactRow
              key={a.id}
              artifact={a}
              selected={selectedArtifactId === a.id}
              picked={selectedIds.has(a.id)}
              onSelect={() => setSelectedArtifactId(a.id)}
              onTogglePick={() => {
                setSelectedIds((cur) => {
                  const next = new Set(cur)
                  if (next.has(a.id)) next.delete(a.id)
                  else next.add(a.id)
                  return next
                })
              }}
            />
          ))}
          {inboxOnly.length === 0 && <div className="px-2 pt-1 text-[12px] me-text-mute">No artifacts.</div>}
        </div>
        {/* Inbox prev/next pagination — matches the artifacts browser
            pattern. ±10 jumps are enabled when there's more than that
            many pages. */}
        {/* Bottom pagination — same shared state as the top one. */}
        {selectedMatterId === 'inbox' && inboxTotal > 0 && (
          <div className="mt-3">
            <InboxPager
              offset={inboxOffset}
              pageSize={INBOX_PAGE_SIZE}
              total={inboxTotal}
              hasNext={!!inboxQuery.data?.next}
              onJump={inboxJump}
            />
          </div>
        )}
      </section>
      )}

      {/* Detail */}
      <section className="me-scroll overflow-auto bg-[color:var(--me-surface)] p-5">
        {detail.data ? (
          <ArtifactDetail
            artifact={detail.data}
            results={detailResults.data || []}
            allMatters={matters.data || []}
          />
        ) : (
          <div className="text-[12px] me-text-mute">Select an artifact to view its details.</div>
        )}
      </section>

      {/* Chat — floating bubble bottom-right; click to expand. */}
      <ChatBubble
        scopeKind={chatScopeKind}
        scopeId={chatScopeId}
        scopeLabel={chatScopeLabel}
        currentArtifactId={selectedArtifactId}
      />
    </div>
  )
}

function ProposedMatterRow({
  matter, selected, onSelect, onKeep, onArchive, pending,
}: {
  matter: Matter
  selected: boolean
  onSelect: () => void
  onKeep: () => void
  onArchive: () => void
  pending: boolean
}) {
  return (
    <div
      data-selected={selected ? 'true' : undefined}
      className="me-row mb-1 border-l-2 border-[color:var(--me-accent)]"
    >
      <button onClick={onSelect} className="block w-full text-left">
        <div className="flex items-center gap-1.5">
          <Sparkles size={10} className="me-text-mute shrink-0" />
          <span className="truncate font-medium me-text">{matter.name}</span>
        </div>
        <div className="me-mono mt-0.5 truncate text-[10px] me-text-mute">
          {matter.composite_id || `M-${matter.client_seq.toString().padStart(3, '0')}`} · proposed · {matter.matter_type}
        </div>
        {matter.summary && (
          <div className="mt-0.5 line-clamp-2 text-[11px] me-text-dim">
            {matter.summary}
          </div>
        )}
      </button>
      {/* Same outlined style for both, hover lights up. Aligned right. */}
      <div className="mt-1.5 flex gap-1 justify-end">
        <button
          onClick={onKeep}
          disabled={pending}
          className="me-btn me-btn-outline !text-[10px] !px-2 !py-0.5 hover:!bg-[color:var(--me-fresh-bg,var(--me-selected))] hover:!border-[color:var(--me-fresh,var(--me-fg))] hover:!text-[color:var(--me-text)]"
          title="Keep — open this matter for filing"
        >
          <Check size={10} /> Keep
        </button>
        <button
          onClick={onArchive}
          disabled={pending}
          className="me-btn me-btn-outline !text-[10px] !px-2 !py-0.5 hover:!bg-[color:var(--me-stale-bg,var(--me-selected))] hover:!border-[color:var(--me-stale,var(--me-fg))] hover:!text-[color:var(--me-text)]"
          title="Archive — set aside; you can restore it later"
        >
          <Archive size={10} /> Archive
        </button>
      </div>
    </div>
  )
}

// MatterRow + ArchivedMatterRow (old flat-list rows) removed; the
// FolderSidebar's MatterRowCompact handles both active and archived
// rendering now (with the `archived` prop).

function ArtifactRow({
  artifact, selected, picked, onSelect, onTogglePick,
}: {
  artifact: Artifact
  selected: boolean
  picked: boolean
  onSelect: () => void
  onTogglePick: () => void
}) {
  return (
    <div data-selected={selected ? 'true' : undefined} className="me-row mb-1 flex items-start gap-2">
      <input
        type="checkbox"
        checked={picked}
        onChange={onTogglePick}
        onClick={(e) => e.stopPropagation()}
        className="mt-1 shrink-0 cursor-pointer"
        title="Select for bulk run"
      />
      <button onClick={onSelect} className="block flex-1 min-w-0 w-full overflow-hidden text-left">
        <div className="flex items-center gap-2 min-w-0">
          <span className="me-chip uppercase shrink-0">{artifact.artifact_type}</span>
          {artifact.parent_artifact && <Paperclip size={11} className="me-text-mute shrink-0" />}
          <span className="truncate font-medium me-text flex-1 min-w-0">{artifact.subject || '(no subject)'}</span>
          {artifact.duplicate_count > 0 && (
            <span
              className="me-chip shrink-0 me-mono"
              title={`Same email also received in ${artifact.duplicate_count} other mailbox${artifact.duplicate_count > 1 ? 'es' : ''}`}
            >
              +{artifact.duplicate_count} dupe
            </span>
          )}
          {artifact.visibility === 'restricted' && (
            <span className="me-chip me-chip-stale shrink-0" title="Restricted visibility"><Lock size={10} /></span>
          )}
        </div>
        <div className="me-mono mt-0.5 truncate text-[11px] me-text-mute">
          {artifact.from_name || artifact.from_email || '—'}
          {artifact.source_date && ` · ${formatDateShort(artifact.source_date)} (${formatRelative(artifact.source_date)})`}
          {artifact.matter_ids.length > 0 && ` · ${artifact.matter_ids.length} matter${artifact.matter_ids.length > 1 ? 's' : ''}`}
        </div>
      </button>
    </div>
  )
}

function ArtifactDetail({
  artifact,
  results,
  allMatters,
}: {
  artifact: ArtifactDetail
  results: Awaited<ReturnType<typeof resultsApi.list>>
  allMatters: Matter[]
}) {
  const matterById = useMemo(() => new Map(allMatters.map((m) => [m.id, m])), [allMatters])
  return (
    <article>
      <div className="mb-2 flex items-center gap-2">
        <span className="me-chip uppercase">{artifact.artifact_type}</span>
        <span className="me-chip me-mono">{artifact.source_type}</span>
        <span className="me-mono text-[11px] me-text-mute">{artifact.composite_id}</span>
        {artifact.visibility === 'restricted' && (
          <span className="me-chip me-chip-stale flex items-center gap-1"><Lock size={10} /> restricted</span>
        )}
      </div>
      <div className="flex items-start justify-between gap-2">
        <h2 className="text-[18px] font-medium me-text leading-snug flex-1 min-w-0 break-words">
          <EditableText
            value={artifact.subject || ''}
            target={{ type: 'artifact', id: artifact.id, field: 'subject' }}
            mode="inline"
            maxLength={998}
            invalidateKeys={[['artifact', artifact.id], ['artifactsPage']]}
            placeholder="(no subject)"
            copyOnClick
          />
        </h2>
        <Link
          to={`/artifact/${artifact.id}`}
          onClick={() => markScrollPendingRestore()}
          className="me-btn me-btn-outline shrink-0"
          title="Open full viewer"
        >
          <ExternalLink size={11} /> View
        </Link>
      </div>
      <div className="mt-1 text-[12px] me-text-dim">
        {artifact.from_name && <span className="font-medium">{artifact.from_name}</span>}
        {artifact.from_name && ' · '}
        <span className="me-mono">{artifact.from_email}</span>
        {artifact.source_date && <span> · {new Date(artifact.source_date).toLocaleString()}</span>}
      </div>

      {/* Classifications grid */}
      {results.length > 0 && (
        <div className="mt-5">
          <div className="me-section-h">Classifications</div>
          <div className="grid grid-cols-2 gap-1.5">
            {results.map((r) => (
              <ClassificationCard key={r.id} r={r} />
            ))}
          </div>
        </div>
      )}

      {/* Matter assignments — wait until the matters query has loaded
          before rendering names. Without this guard, the chips briefly
          show #<db_id> on reload (matterById is empty until matters
          resolves) and then flicker to the real name + confidence.
          allMatters being empty AND matter_assignments being non-empty
          means matters is still loading; show a subtle placeholder
          row so the section doesn't pop in suddenly. */}
      {artifact.matter_assignments?.length > 0 && (
        <div className="mt-5">
          <div className="me-section-h">Matters</div>
          <div className="flex flex-wrap gap-1.5">
            {allMatters.length === 0 ? (
              <span className="me-chip me-text-mute italic">loading…</span>
            ) : (
              artifact.matter_assignments.map((am, i) => {
                const m = matterById.get(am.matter)
                if (!m) return null  // matter not in this user's view (privacy/deleted) — skip
                return (
                  <span key={i} className="me-chip">
                    {m.name}
                    {am.assigned_by === 'ai' && (
                      <span className="me-text-mute me-mono"> · {Math.round((am.confidence || 0) * 100)}%</span>
                    )}
                  </span>
                )
              })
            )}
          </div>
        </div>
      )}

      {/* Summary — the AI-generated 2-4 sentence digest of the
          artifact's content. Editable inline (manual edits feed back
          into the summarize_artifact prompt on next run). Always
          rendered (placeholder when empty) so users can author one
          when the AI hasn't generated anything yet. */}
      <div className="mt-5">
        <div className="me-section-h">Summary</div>
        <div className="me-card p-3 text-[12.5px] leading-snug me-text whitespace-pre-wrap">
          <EditableText
            value={artifact.ai_summary || ''}
            target={{ type: 'artifact', id: artifact.id, field: 'ai_summary' }}
            mode="block"
            multiline
            maxLength={4000}
            invalidateKeys={[['artifact', artifact.id]]}
            placeholder="(no AI summary yet)"
            copyOnClick
          />
        </div>
      </div>

      <div className="my-5 border-t me-border" />

      {/* Same previewer as the full ArtifactViewer page — image inline,
          PDF embed, .docx rendered via mammoth, HTML for emails, plain
          text, or a binary-fallback download. The Content header (label
          + mode toggle + download + copy) is rendered by BodyPreview. */}
      <ArtifactBodyPanel artifact={artifact} />

      {/* Cross-mailbox dedup footer. When this artifact is the primary
          for a duplicate group, list which other mailboxes received the
          same email. When it IS a duplicate, point at the primary so
          the user can jump to it. */}
      {artifact.duplicates?.length > 0 && (
        <div className="mt-3 me-card p-3 text-[12px] me-text-dim">
          <div className="me-mono uppercase tracking-wider me-text-mute mb-1 text-[10.5px]">
            Also received in
          </div>
          <ul className="space-y-0.5">
            {artifact.duplicates.map((d) => (
              <li key={d.id} className="me-mono">
                {d.source_account_email || `(unknown mailbox) #${d.id}`}
              </li>
            ))}
          </ul>
        </div>
      )}
      {artifact.is_duplicate_of && (
        <div className="mt-3 me-card p-3 text-[12px] me-text-dim">
          This artifact is a cross-mailbox duplicate. The primary
          (with the AI analysis) is{' '}
          <Link
            to={`/artifact/${artifact.is_duplicate_of}`}
            className="underline"
          >
            artifact #{artifact.is_duplicate_of}
          </Link>
          .
        </div>
      )}

      {artifact.comments?.length > 0 && (
        <div className="mt-5">
          <div className="me-section-h">Comments ({artifact.comments.length})</div>
          <ul className="space-y-1">
            {artifact.comments.slice(0, 10).map((c, i) => (
              <li key={i} className="me-card p-2.5">
                <div className="flex items-center justify-between">
                  <span className="me-mono text-[11px] me-text-dim">{(c as any).author_name || (c as any).author_email}</span>
                  {(c as any).resolved && <span className="me-chip me-chip-fresh">resolved</span>}
                </div>
                <div className="mt-1 text-[12px] me-text">{(c as any).text}</div>
                {(c as any).quoted && (
                  <div className="mt-1 me-mono text-[11px] me-text-mute italic">re: "{(c as any).quoted.slice(0, 120)}"</div>
                )}
              </li>
            ))}
            {artifact.comments.length > 10 && (
              <li className="me-text-mute text-[11px]">+ {artifact.comments.length - 10} more</li>
            )}
          </ul>
        </div>
      )}

      {artifact.redlines?.length > 0 && (
        <div className="mt-5">
          <div className="me-section-h">Redlines ({artifact.redlines.length})</div>
          <ul className="space-y-1">
            {artifact.redlines.slice(0, 8).map((r, i) => (
              <li key={i} className="me-card p-2.5">
                <div className="flex items-center justify-between">
                  <span className="me-mono text-[11px] me-text-dim">{(r as any).author_name}</span>
                  <span className="me-chip">{(r as any).action}</span>
                </div>
                <div className="mt-1 me-mono text-[11px] me-text leading-snug">{(r as any).text}</div>
                {(r as any).surrounding_context && (
                  <div className="mt-1 me-mono text-[10px] me-text-mute italic">"{(r as any).surrounding_context.slice(0, 100)}"</div>
                )}
              </li>
            ))}
            {artifact.redlines.length > 8 && (
              <li className="me-text-mute text-[11px]">+ {artifact.redlines.length - 8} more</li>
            )}
          </ul>
        </div>
      )}
    </article>
  )
}

/** Content section in the right pane — header + toggle + download + copy
 *  are all rendered by BodyPreview; we only own the copy callback wired
 *  to the shared formatter. */
function ArtifactBodyPanel({ artifact }: { artifact: ArtifactDetail }) {
  const [copied, setCopied] = useState(false)
  const onCopy = async () => {
    const ok = await copyArtifactToClipboard(artifact)
    if (ok) {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }
  }
  return (
    <BodyPreview
      mime={artifact.mime_type || ''}
      cachedContent={artifact.cached_content || ''}
      bodyHtml={artifact.body_html || ''}
      cachedFileUrl={artifact.cached_file_url || null}
      cachedFileSize={artifact.cached_file_size}
      storageMode={artifact.content_storage_mode || ''}
      sourceType={artifact.source_type}
      sourceExternalId={artifact.source_external_id}
      pdfHeight="min(900px, 80vh)"
      onCopy={onCopy}
      copied={copied}
    />
  )
}


function InboxPager({
  offset, pageSize, total, hasNext, onJump,
}: {
  offset: number
  pageSize: number
  total: number
  hasNext: boolean
  onJump: (deltaPages: number) => void
}) {
  const pageNum = Math.floor(offset / pageSize) + 1
  const lastPage = Math.max(1, Math.ceil(total / pageSize))
  return (
    <div className="flex items-center justify-end gap-1.5 text-[10px]">
      <span className="me-mono me-text-mute mr-1">
        {`${(offset + 1).toLocaleString()}–${Math.min(offset + pageSize, total).toLocaleString()}`} of {total.toLocaleString()}
        {' · '}p {pageNum.toLocaleString()}/{lastPage.toLocaleString()}
      </span>
      <button
        disabled={offset === 0}
        onClick={() => onJump(-10)}
        className="me-btn me-btn-outline !text-[10px] !px-1.5 !py-0.5"
        title="Back 10 pages"
      >−10</button>
      <button
        disabled={offset === 0}
        onClick={() => onJump(-1)}
        className="me-btn me-btn-outline !text-[10px] !px-1.5 !py-0.5"
      >prev</button>
      <button
        disabled={!hasNext}
        onClick={() => onJump(1)}
        className="me-btn me-btn-outline !text-[10px] !px-1.5 !py-0.5"
      >next</button>
      <button
        disabled={pageNum >= lastPage}
        onClick={() => onJump(10)}
        className="me-btn me-btn-outline !text-[10px] !px-1.5 !py-0.5"
        title="Forward 10 pages"
      >+10</button>
    </div>
  )
}


function formatRelative(iso: string): string {
  const d = new Date(iso)
  const now = new Date()
  const days = (now.getTime() - d.getTime()) / 86400000
  if (days < 1) return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
  if (days < 7) return `${Math.floor(days)}d ago`
  if (days < 30) return `${Math.floor(days / 7)}w ago`
  if (days < 365) return `${Math.floor(days / 30)}mo ago`
  return `${Math.floor(days / 365)}y ago`
}

function formatDateShort(iso: string): string {
  // "Jun 2, 2025 5:00pm" — concise, locale-neutral. Used in the
  // inbox row so we always see the actual date, not just relative.
  const d = new Date(iso)
  const date = d.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' })
  const time = d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }).toLowerCase().replace(' ', '')
  return `${date} ${time}`
}

declare global {
  interface Window {
    Awaited?: never
  }
}


// MatterBrief was the inline summary block above the artifact list.
// Replaced by the matter header in MatterTimelinePane (which mirrors
// the prototype). Keeping the file lean — if narrative_groups become
// useful in the new pane, port that block in.
